'use client';
import { jsx } from 'react/jsx-runtime';
import { defineStyle } from '@chakra-ui/styled-system';
import { keyframes } from '@emotion/react';
import { useMemo, cloneElement } from 'react';
import { useCheckboxContext, useCheckboxStyles } from './checkbox-context.mjs';
import { CheckboxIcon } from './checkbox-icon.mjs';
import { useInitialAnimationState } from './use-initial-animation-state.mjs';
import { forwardRef } from '../system/forward-ref.mjs';
import { chakra } from '../system/factory.mjs';

const checkAnim = keyframes({
  from: {
    opacity: 0,
    strokeDashoffset: 16,
    transform: "scale(0.95)"
  },
  to: {
    opacity: 1,
    strokeDashoffset: 0,
    transform: "scale(1)"
  }
});
const indeterminateOpacityAnim = keyframes({
  from: {
    opacity: 0
  },
  to: {
    opacity: 1
  }
});
const indeterminateScaleAnim = keyframes({
  from: {
    transform: "scaleX(0.65)"
  },
  to: {
    transform: "scaleX(1)"
  }
});
const CheckboxControl = forwardRef(
  function CheckboxControl2(props, ref) {
    const { icon = /* @__PURE__ */ jsx(CheckboxIcon, {}), iconColor, iconSize, ...restProps } = props;
    const { getCheckboxProps, state } = useCheckboxContext();
    const styles = useCheckboxStyles();
    const shouldAnimate = useInitialAnimationState(state.isChecked);
    const iconStyles = useMemo(
      () => defineStyle({
        animation: !shouldAnimate ? void 0 : state.isIndeterminate ? `${indeterminateOpacityAnim} 20ms linear, ${indeterminateScaleAnim} 200ms linear` : `${checkAnim} 200ms linear`,
        fontSize: iconSize,
        color: iconColor,
        ...styles.icon
      }),
      [iconColor, iconSize, shouldAnimate, state.isIndeterminate, styles.icon]
    );
    const clonedIcon = cloneElement(icon, {
      __css: iconStyles,
      isIndeterminate: state.isIndeterminate,
      isChecked: state.isChecked
    });
    return /* @__PURE__ */ jsx(
      chakra.span,
      {
        ref,
        ...restProps,
        __css: styles.control,
        className: "chakra-checkbox__control",
        ...getCheckboxProps(),
        children: clonedIcon
      }
    );
  }
);
CheckboxControl.displayName = "CheckboxControl";

export { CheckboxControl };
